import * as React from 'react';
import { CalendarIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils/format';

export interface DateRange {
  from?: Date;
  to?: Date;
}

interface DateRangePickerProps {
  from?: Date;
  to?: Date;
  onChange: (range: DateRange) => void;
  shortcuts?: boolean;
  className?: string;
}

export function DateRangePicker({
  from,
  to,
  onChange,
  shortcuts = true,
  className,
}: DateRangePickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = React.useState(false);

  const hasValue = from || to;

  const displayText = React.useMemo(() => {
    if (from && to) {
      return `${formatDate(from)} – ${formatDate(to)}`;
    }
    if (from) return `${t('dateRange.from', 'Ab')} ${formatDate(from)}`;
    if (to) return `${t('dateRange.to', 'Bis')} ${formatDate(to)}`;
    return t('dateRange.placeholder', 'Zeitraum wählen...');
  }, [from, to, t]);

  const handleSelect = React.useCallback(
    (range: { from?: Date; to?: Date } | undefined) => {
      onChange({ from: range?.from, to: range?.to });
    },
    [onChange],
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange({ from: undefined, to: undefined });
    },
    [onChange],
  );

  const handleShortcut = React.useCallback(
    (getRange: () => DateRange) => {
      onChange(getRange());
      setOpen(false);
    },
    [onChange],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'justify-start text-left font-normal h-9 px-3',
            !hasValue && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          <span className="truncate text-sm">{displayText}</span>
          {hasValue && (
            <XIcon
              className="ml-auto h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
              onClick={handleClear}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {shortcuts && (
          <div className="flex flex-wrap gap-1 border-b p-2">
            <ShortcutButton
              label={t('dateShortcuts.today', 'Heute')}
              onClick={() => handleShortcut(getToday)}
            />
            <ShortcutButton
              label={t('dateShortcuts.last7days', 'Letzte 7 Tage')}
              onClick={() => handleShortcut(getLast7Days)}
            />
            <ShortcutButton
              label={t('dateShortcuts.last30days', 'Letzte 30 Tage')}
              onClick={() => handleShortcut(getLast30Days)}
            />
            <ShortcutButton
              label={t('dateShortcuts.thisMonth', 'Dieser Monat')}
              onClick={() => handleShortcut(getThisMonth)}
            />
          </div>
        )}
        <Calendar
          mode="range"
          selected={from || to ? { from, to } : undefined}
          onSelect={handleSelect}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}

function ShortcutButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClick}>
      {label}
    </Button>
  );
}

// --- Shortcut-Generatoren ---

function getToday(): DateRange {
  const today = new Date();
  return { from: today, to: today };
}

function getLast7Days(): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 6);
  return { from, to };
}

function getLast30Days(): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from, to };
}

function getThisMonth(): DateRange {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from, to: now };
}
